import { del, get, patch, post, put } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';

function toStringParams(params: Record<string, unknown>): Record<string, string> {
  const result: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      result[key] = String(value);
    }
  }
  return result;
}

/**
 * Validation error details structure from backend Zod validation
 */
interface ValidationErrorDetails {
  [field: string]: string[] | ValidationErrorDetails;
}

/**
 * API Error class for consistent error handling
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public code?: string,
    public details?: ValidationErrorDetails
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();

    if (!token) {
      throw new Error('No authentication token available');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  } catch {
    throw new ApiError('Authentication required', 401, 'UNAUTHORIZED');
  }
}

export async function apiGet<T>(apiName: string, path: string, queryParams?: Record<string, unknown>): Promise<T> {
  const resp = await get({
    apiName,
    path,
    options: {
      headers: await getAuthHeaders(),
      ...(queryParams ? { queryParams: toStringParams(queryParams) } : undefined)
    }
  }).response;
  return (await resp.body.json()) as unknown as T;
}

export async function apiPost<T>(apiName: string, path: string, body?: unknown): Promise<T> {
  const resp = await post({
    apiName,
    path,
    options: {
      headers: await getAuthHeaders(),
      ...(body !== undefined ? { body } : undefined)
    }
  }).response;
  return (await resp.body.json()) as unknown as T;
}

export async function apiPut<T>(apiName: string, path: string, body?: unknown): Promise<T> {
  const resp = await put({
    apiName,
    path,
    options: {
      headers: await getAuthHeaders(),
      ...(body !== undefined ? { body } : undefined)
    }
  }).response;
  return (await resp.body.json()) as unknown as T;
}

export async function apiDel<T = void>(apiName: string, path: string): Promise<T> {
  const resp = await del({ apiName, path, options: { headers: await getAuthHeaders() } }).response;
  try {
    return (await resp.body.json()) as unknown as T;
  } catch {
    return undefined as unknown as T;
  }
}

export async function apiPostFormData<T>(apiName: string, path: string, formData: FormData): Promise<T> {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  if (!token) throw new ApiError('Authentication required', 401, 'UNAUTHORIZED');

  const resp = await post({
    apiName,
    path,
    options: {
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    }
  }).response;
  return (await resp.body.json()) as unknown as T;
}

export async function apiPatch<T>(apiName: string, path: string, body?: unknown): Promise<T> {
  const resp = await patch({
    apiName,
    path,
    options: {
      headers: await getAuthHeaders(),
      ...(body !== undefined ? { body } : undefined)
    }
  }).response;
  return (await resp.body.json()) as unknown as T;
}
